import {
    AgGridCellTooltipComponent,
    AgGridCellTooltipValueGetter,
} from '@components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from '@components/CustomTableFilter';
import { type CatalogRatioDataForList, type CatalogRatioShortData, RatioTypeEnum, SortEnumType } from '@gql/graphql';
import { formatNumberToRu } from '@utils';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { addRatiosModalCodeFilterGql, addRatiosModalNameFilterGql } from '../../AddRatiosModal/addRatiosModalQueries';
import { RatiosTableCellRenderCheckbox } from '../components/RatiosTableCellRenderCheckbox';

type TUseMassApplicationRatiosColumns = {
    data: CatalogRatioDataForList[] | null;
    checkedIds: Record<string, CatalogRatioShortData | null>;
    changeFilterHandler: (filterData: any) => void;
    handleChangeCheckbox: (row: CatalogRatioShortData) => ({ checked }: { checked: boolean }) => void;
    ratioType?: RatioTypeEnum;
    ratioGroupId?: string | number;
    subGroupsNames: string[];
    isConstructionWorksGroupSelected: boolean;
    sortColDef?: ColDef;
    getCanCheckRow?: (data: CatalogRatioShortData) => boolean;
};

export const useMassApplicationRatiosColumns = ({
    data,
    checkedIds,
    changeFilterHandler,
    handleChangeCheckbox,
    ratioType,
    ratioGroupId,
    subGroupsNames,
    isConstructionWorksGroupSelected,
    sortColDef,
    getCanCheckRow,
}: TUseMassApplicationRatiosColumns) => {
    const filterWhereVariables = useMemo(
        () => ({
            ratioType: {
                eq: ratioType,
            },
            ...(ratioType !== RatioTypeEnum.TechPartCoef && !isConstructionWorksGroupSelected
                ? {
                      catalogRatioGroupId: { eq: ratioGroupId },
                  }
                : {}),
            ...(ratioType === RatioTypeEnum.DismantlingCoef && isConstructionWorksGroupSelected && subGroupsNames.length
                ? {
                      and: [
                          {
                              catalogRatioGroupName: {
                                  in: subGroupsNames,
                              },
                          },
                      ],
                      hasCosts: { eq: false },
                  }
                : {}),
        }),
        [isConstructionWorksGroupSelected, ratioGroupId, ratioType, subGroupsNames],
    );

    const columns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 46,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                cellRenderer: RatiosTableCellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox,
                    currentRowData: data,
                    getCanCheckRow,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 130,
                initialSort: 'asc',
                ...sortColDef,
                cellRenderer: ({ value }: { value: string }) => value || '',
                ...((ratioType === RatioTypeEnum.TechPartCoef || !!ratioGroupId) && {
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        dataAccessor: 'catalogRatios',
                        customRequestId: 'massApplyRatiosModalCodeFilter',
                        query: addRatiosModalCodeFilterGql,
                        additionalWhereVariables: filterWhereVariables,
                        variables: {
                            order: [
                                {
                                    code: SortEnumType.Asc,
                                },
                            ],
                            take: 1000,
                        },
                        whereVariables: ['code'],
                        childFieldAccessor: 'code',
                        columns: [
                            {
                                headerName: 'Шифр',
                                field: 'code',
                                checkboxSelection: true,
                                width: 400,
                            },
                        ],
                        changeFilterHandler,
                    },
                }),
            },
            {
                headerName: 'Наименование',
                field: 'name',
                width: 400,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                cellClass: 'ellipsis',
                spanHeaderHeight: true,
                ...sortColDef,
                cellRenderer: ({ value }: { value: string }) => <div>{value || ''}</div>,
                ...((ratioType === RatioTypeEnum.TechPartCoef || !!ratioGroupId) && {
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        dataAccessor: 'catalogRatios',
                        customRequestId: 'massApplyRatiosModalNameFilter',
                        query: addRatiosModalNameFilterGql,
                        additionalWhereVariables: filterWhereVariables,
                        variables: {
                            order: [
                                {
                                    name: SortEnumType.Asc,
                                },
                            ],
                            take: 1000,
                        },
                        whereVariables: ['name'],
                        childFieldAccessor: 'name',
                        columns: [
                            {
                                headerName: 'Наименование',
                                field: 'name',
                                checkboxSelection: true,
                                width: 400,
                            },
                        ],
                        changeFilterHandler,
                    },
                }),
            },
            {
                headerName: 'К озп',
                field: 'kozp',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К эмм',
                field: 'kem',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К нр',
                field: 'knr',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К сп',
                field: 'ksp',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К мат',
                autoHeight: true,
                field: 'kmat',
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К тз',
                field: 'ktz',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К тз зпм',
                field: 'ktzzpm',
                autoHeight: true,
                align: 'center',
                width: 100,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
        ],
        [
            checkedIds,
            handleChangeCheckbox,
            data,
            getCanCheckRow,
            sortColDef,
            ratioType,
            ratioGroupId,
            filterWhereVariables,
            changeFilterHandler,
        ],
    );

    return { columns };
};
